import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

const getAuthToken = (): string | null => {
  const authStorage = localStorage.getItem('auth-storage');
  if (!authStorage) return null;
  const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
  return parsed.state?.token || null;
};

export interface IngestEnqueuedResponse {
  jobId: string | number;
  projectId: string;
  status: 'queued';
  batchSize: number;
  dryRun: boolean;
}

export interface BuildingsStatusResponse {
  status: 'none' | 'queued' | 'processing' | 'ready' | 'error';
  meta: Record<string, unknown> | null;
  centroidCount: number;
}

export interface BuildingsClearResponse {
  deleted: number;
  associationsDeleted: number;
}

export interface IngestOptions {
  clearPrevious?: boolean;
  dryRun?: boolean;
  batchSize?: number;
}

/**
 * buildingIngestionApi — Cliente HTTP para ingesta edilicia
 *
 * Endpoints:
 * - POST /projects/:id/buildings/ingest   → multipart/form-data
 * - GET  /projects/:id/buildings/status
 * - DELETE /projects/:id/buildings
 */
export const buildingIngestionApi = {
  async ingest(
    projectId: string,
    file: File,
    opts: IngestOptions = {},
  ): Promise<IngestEnqueuedResponse> {
    const token = getAuthToken();
    const formData = new FormData();
    formData.append('file', file);
    if (opts.clearPrevious) formData.append('clearPrevious', 'true');
    if (opts.dryRun) formData.append('dryRun', 'true');
    if (opts.batchSize !== undefined) formData.append('batchSize', String(opts.batchSize));

    const response = await axios.post<IngestEnqueuedResponse>(
      `${API_URL}/projects/${projectId}/buildings/ingest`,
      formData,
      {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          // No fijar Content-Type — axios/browser lo setea con el boundary correcto
        },
      },
    );
    return response.data;
  },

  async getStatus(projectId: string): Promise<BuildingsStatusResponse> {
    const token = getAuthToken();
    const response = await axios.get<BuildingsStatusResponse>(
      `${API_URL}/projects/${projectId}/buildings/status`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    return response.data;
  },

  async clear(projectId: string): Promise<BuildingsClearResponse> {
    const token = getAuthToken();
    const response = await axios.delete<BuildingsClearResponse>(
      `${API_URL}/projects/${projectId}/buildings`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    return response.data;
  },
};
